import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';
import { safeHtml } from '../util/safeHtml';
import { StatsWidget, OnlineWidget, ContributorsWidget, TrendingWidget, NewestMemberWidget, ArticlesWidget, TagsListWidget } from '../widgetComponents';
import { NavWidget, ComposeWidget } from './layoutWidgets';

// Flarum exposes Mithril globally as `m`; the simple widget views use it at
// runtime. Declared here to avoid bundling a second copy of mithril.

// `label` values are translation KEY suffixes (forum.widget_*), resolved when
// the tray/inspector renders, so every widget name is translatable.

// Default texts are getters so they resolve through the translator when the
// widget renders (settings fall back to them per render), not at registration.
const dflt = key => extractText(app.translator.trans('ernestdefoe-bespoke.forum.widget_default_' + key));

// Most widgets can go anywhere: the top of the page, the full-width header
// band, above the discussion list, the sidebar, below the list, or the footer.
const ANYWHERE = ['top', 'header', 'above-list', 'sidebar', 'below-list', 'footer'];

// The page's own layout pieces sit in the content column or the sidebar.
const IN_CONTENT = ['top', 'above-list', 'sidebar', 'below-list'];

/**
 * Registers the built-in widget set. Each is a Mithril component + a settings
 * schema the inspector renders generically. Third parties add more via
 * `app.bespoke.widgets.add(...)` in their own initializer.
 */
export default function registerBuiltinWidgets(registry) {
  registry.add({
    type: 'hero',
    label: 'widget_hero',
    icon: '🦸',
    zones: ['header', 'top'],
    schema: [{
      key: 'heading',
      type: 'text',
      label: 'Heading',
      get default() {
        return dflt('hero_heading');
      }
    }, {
      key: 'subheading',
      type: 'textarea',
      label: 'Subheading',
      default: ''
    }, {
      key: 'cta',
      type: 'text',
      label: 'Button text',
      get default() {
        return dflt('hero_cta');
      }
    }, {
      key: 'image',
      type: 'image',
      label: 'Background image'
    }],
    component: {
      view: v => m('.Bespoke-w-hero', m('h1', v.attrs.settings.heading), m('p', v.attrs.settings.subheading))
    }
  }).add({
    type: 'stats',
    label: 'widget_stats',
    icon: '📊',
    zones: ANYWHERE,
    schema: [{
      key: 'title',
      type: 'text',
      label: 'Title',
      get default() {
        return dflt('stats_title');
      }
    }],
    component: StatsWidget
  }).add({
    type: 'online',
    label: 'widget_online',
    icon: '🟢',
    zones: ANYWHERE,
    schema: [{
      key: 'title',
      type: 'text',
      label: 'Title',
      get default() {
        return dflt('online_title');
      }
    }, {
      key: 'showVisitors',
      type: 'toggle',
      label: 'Show visitor count',
      default: true
    }],
    component: OnlineWidget
  }).add({
    type: 'contributors',
    label: 'widget_contributors',
    icon: '🏆',
    zones: ANYWHERE,
    schema: [{
      key: 'title',
      type: 'text',
      label: 'Title',
      get default() {
        return dflt('contributors_title');
      }
    }, {
      key: 'count',
      type: 'number',
      label: 'How many',
      default: 5
    }, {
      key: 'period',
      type: 'select',
      label: 'Period',
      default: 'month',
      options: [{
        value: 'week',
        label: 'This week'
      }, {
        value: 'month',
        label: 'This month'
      }, {
        value: 'all',
        label: 'All time'
      }]
    }, {
      key: 'metric',
      type: 'select',
      label: 'Rank by',
      default: 'posts',
      options: [{
        value: 'posts',
        label: 'Posts'
      }, {
        value: 'likes',
        label: 'Likes received'
      }, {
        value: 'discussions',
        label: 'Discussions started'
      }]
    }],
    component: ContributorsWidget
  }).add({
    type: 'newest',
    label: 'widget_newest',
    icon: '👋',
    zones: ANYWHERE,
    schema: [{
      key: 'title',
      type: 'text',
      label: 'Title',
      get default() {
        return dflt('newest_title');
      }
    }, {
      key: 'count',
      type: 'number',
      label: 'How many',
      default: 5
    }, {
      key: 'layout',
      type: 'select',
      label: 'Layout',
      default: 'list',
      options: [{
        value: 'list',
        label: 'List (with join date)'
      }, {
        value: 'grid',
        label: 'Avatar grid'
      }]
    }],
    component: NewestMemberWidget
  }).add({
    type: 'trending',
    label: 'widget_trending',
    icon: '🔥',
    zones: ANYWHERE,
    schema: [{
      key: 'title',
      type: 'text',
      label: 'Title',
      get default() {
        return dflt('trending_title');
      }
    }, {
      key: 'count',
      type: 'number',
      label: 'How many',
      default: 5
    }, {
      key: 'window',
      type: 'select',
      label: 'Trending over',
      default: '48h',
      options: [{
        value: '24h',
        label: 'Last 24 hours'
      }, {
        value: '48h',
        label: 'Last 48 hours'
      }, {
        value: '7d',
        label: 'Last 7 days'
      }]
    }, {
      key: 'tag',
      type: 'text',
      label: 'Filter by tag slug (optional)',
      default: ''
    }],
    component: TrendingWidget
  }).add({
    type: 'cta',
    label: 'widget_cta',
    icon: '📣',
    zones: ANYWHERE,
    schema: [{
      key: 'heading',
      type: 'text',
      label: 'Heading',
      get default() {
        return dflt('cta_heading');
      }
    }, {
      key: 'body',
      type: 'textarea',
      label: 'Body',
      default: ''
    }, {
      key: 'button',
      type: 'text',
      label: 'Button',
      get default() {
        return dflt('cta_button');
      }
    }, {
      key: 'url',
      type: 'text',
      label: 'Button link',
      default: ''
    }],
    component: {
      view: v => m('.Bespoke-w-cta', [m('h4', v.attrs.settings.heading), v.attrs.settings.body ? m('p', v.attrs.settings.body) : null, m('a.Button.Button--primary', {
        href: v.attrs.settings.url || '#'
      }, v.attrs.settings.button)])
    }
  }).add({
    // Stored id stays 'markdown' for backwards compatibility with already-saved
    // layouts. The content is plain text with a safe subset of HTML honoured
    // (headings, links, images, lists, tables…); see util/safeHtml.
    type: 'markdown',
    label: 'widget_text',
    icon: '✍️',
    zones: ['hero', ...ANYWHERE],
    schema: [{
      key: 'title',
      type: 'text',
      label: 'Title',
      default: ''
    }, {
      key: 'md',
      type: 'textarea',
      label: 'Content (text or basic HTML)',
      default: ''
    }],
    component: {
      view: v => m('.Bespoke-w-md', [v.attrs.settings.title ? m('h4', v.attrs.settings.title) : null, m('.Bespoke-w-mdBody', safeHtml(v.attrs.settings.md))])
    }
  }).add({
    // Stored id stays 'tags' — instances placed while this was a stub pick up
    // the real rendering (and the new settings' defaults) automatically.
    type: 'tags',
    label: 'widget_tags',
    icon: '🏷️',
    zones: ANYWHERE,
    schema: [{
      key: 'title',
      type: 'text',
      label: 'Title',
      get default() {
        return dflt('tags_title');
      }
    }, {
      key: 'style',
      type: 'select',
      label: 'Style',
      default: 'chips',
      options: [{
        value: 'chips',
        label: 'Chips'
      }, {
        value: 'list',
        label: 'List (with counts)'
      }, {
        value: 'forums',
        label: 'Forum categories (with subforums)'
      }]
    }, {
      key: 'count',
      type: 'number',
      label: 'How many (0 = all)',
      default: 12
    }, {
      key: 'showCounts',
      type: 'toggle',
      label: 'Show discussion counts',
      default: true
    }, {
      key: 'topOnly',
      type: 'toggle',
      label: 'Top-level tags only',
      default: true
    }],
    component: TagsListWidget
  }).add({
    // Bespoke's nav pills / tiles. Placing it takes them out of the bar at
    // the top of the index, so they appear once, wherever this sits.
    type: 'nav',
    label: 'widget_nav',
    icon: '🧭',
    zones: IN_CONTENT,
    desktopOnly: true,
    schema: [{
      key: 'title',
      type: 'text',
      label: 'Title',
      default: ''
    }],
    component: NavWidget
  }).add({
    // The New Discussion button, plain or as a "what are you working on"
    // prompt bar. Placing it takes the button out of the bar at the top.
    type: 'compose',
    label: 'widget_compose',
    icon: '✏️',
    zones: IN_CONTENT,
    desktopOnly: true,
    schema: [{
      key: 'style',
      type: 'select',
      label: 'Style',
      default: 'button',
      options: [{
        value: 'button',
        label: 'Button'
      }, {
        value: 'prompt',
        label: 'Prompt bar'
      }]
    }, {
      key: 'prompt',
      type: 'text',
      label: 'Prompt text',
      get default() {
        return dflt('compose_prompt');
      }
    }],
    component: ComposeWidget
  });

  // ---- Integration widgets (registered only when the partner extension is on) ----
  if (hasExtension('ernestdefoe-page-builder')) {
    registry.add({
      type: 'articles',
      label: 'widget_articles',
      icon: '📰',
      zones: ['hero', ...ANYWHERE],
      schema: [{
        key: 'title',
        type: 'text',
        label: 'Heading',
        get default() {
          return dflt('articles_title');
        }
      }, {
        key: 'count',
        type: 'number',
        label: 'How many',
        default: 3
      }, {
        key: 'style',
        type: 'select',
        label: 'Style',
        default: 'cards',
        options: [{
          value: 'cards',
          label: 'Cards'
        }, {
          value: 'carousel',
          label: 'Carousel'
        }, {
          value: 'list',
          label: 'Compact list'
        }, {
          value: 'featured',
          label: 'Featured + list'
        }]
      }, {
        key: 'category',
        type: 'text',
        label: 'Filter by category slug (optional)',
        default: ''
      }, {
        key: 'showCover',
        type: 'toggle',
        label: 'Show cover images',
        default: true
      }, {
        key: 'showExcerpt',
        type: 'toggle',
        label: 'Show excerpts',
        default: true
      }],
      component: ArticlesWidget
    });
  }
}

/** Is a Flarum extension installed & enabled in this frontend? */
function hasExtension(id) {
  return typeof flarum !== 'undefined' && !!flarum.extensions && id in flarum.extensions;
}
flarum.reg.add('ernestdefoe-bespoke', 'forum/widgets', registerBuiltinWidgets);